import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Form } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { StepIndicator } from "./components/StepIndicator";
import { FormNavigation } from "./components/FormNavigation";
import { FormContent } from "./components/FormContent";
import { TaxReturnReference } from "./components/TaxReturnReference";
import { PreCheckSystem } from "./components/PreCheckSystem";
import { AutoSaveIndicator } from "./components/AutoSaveIndicator";
import { ProgressIndicator } from "./components/ProgressIndicator";
import { useAutoSave } from "./hooks/useAutoSave";
import { useCompletionProgress } from "./hooks/useCompletionProgress";
import { useTaxReturnForm } from "./hooks/useTaxReturnForm";
import { TaxReturnFormValues } from "./types/tax-return";

const steps = [
  { 
    id: "personal", 
    title: "Personal Details",
    description: "Your basic information",
    icon: "personal" as const,
  },
  { 
    id: "banking", 
    title: "Banking Details",
    description: "For your tax refund",
    icon: "banking" as const,
  },
  { 
    id: "documents", 
    title: "Document Upload",
    description: "Upload your tax documents",
    icon: "documents" as const,
  },
  { 
    id: "service", 
    title: "Service Selection",
    description: "Choose your preferred service",
    icon: "service" as const,
  },
  {
    id: "payment",
    title: "Payment",
    description: "Complete your payment",
    icon: "payment" as const,
  }
];

export function TaxReturnForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { form, isLoading, taxReturnId } = useTaxReturnForm();
  const { toast } = useToast();

  // Auto-save functionality
  const { isSaving, lastSaved } = useAutoSave(form, currentStep);
  const progress = useCompletionProgress(form);

  // Subscribe to service number updates
  useEffect(() => {
    if (!taxReturnId) return;

    const channel = supabase
      .channel('tax-return-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tax_return_requests',
          filter: `id=eq.${taxReturnId}`
        },
        (payload) => {
          if (payload.new.service_id && payload.new.service_id !== form.getValues('service_id')) {
            form.setValue('service_id', payload.new.service_id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taxReturnId, form]);

  const handlePreCheckComplete = async (financialYear: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      // Create tax return request
      const { data: taxReturn, error } = await supabase
        .from('tax_return_requests')
        .insert({
          user_id: user.id,
          financial_year: financialYear,
          status: 'draft',
          service_type: 'online',
          pre_check_completed: true,
          pre_check_data: {
            completed_at: new Date().toISOString(),
            selected_year: financialYear
          }
        })
        .select()
        .single();

      if (error) throw error;

      // Show success message
      toast({
        title: "Tax Return Initiated",
        description: "You can now proceed with your tax return submission.",
      });

      setShowForm(true);
    } catch (error: any) {
      console.error('Error creating tax return:', error);
      toast({
        title: "Error",
        description: "Failed to initiate tax return. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      const serviceId = form.getValues('service_id');
      const taxReturnId = form.getValues('id');

      // Update service status
      const { error: serviceError } = await supabase
        .from('services')
        .update({
          status: 'paid'
        })
        .eq('id', serviceId);

      if (serviceError) throw serviceError;

      // Update tax return status
      const { error: taxReturnError } = await supabase
        .from('tax_return_requests')
        .update({
          status: 'paid'
        })
        .eq('id', taxReturnId);

      if (taxReturnError) throw taxReturnError;

      toast({
        title: "Payment successful",
        description: "Your tax return request has been submitted successfully.",
      });
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      toast({
        title: "Error",
        description: "There was a problem updating your payment status. Please contact support.",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: TaxReturnFormValues) => {
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No authenticated user found');
      if (!data.id) throw new Error('No tax return request ID found');
      if (!data.service_id) throw new Error('No service ID found');

      // Update tax return request
      const { error: taxReturnError } = await supabase
        .from('tax_return_requests')
        .update({
          service_type: data.service_type,
          preferred_office: data.preferred_office,
          bsb: data.bsb,
          account_number: data.account_number,
          account_name: data.account_name,
          status: 'pending_payment'
        })
        .eq('id', data.id);

      if (taxReturnError) throw taxReturnError;

      // Update service status
      const { error: serviceError } = await supabase
        .from('services')
        .update({
          status: 'pending'
        })
        .eq('id', data.service_id);

      if (serviceError) throw serviceError;

      setCurrentStep(steps.length - 1);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  if (!showForm) {
    return <PreCheckSystem onComplete={handlePreCheckComplete} />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {taxReturnId && <TaxReturnReference taxReturnId={taxReturnId} />}
      <Card className="border-0 shadow-lg">
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <StepIndicator currentStep={currentStep} steps={steps} />
              <AutoSaveIndicator isSaving={isSaving} lastSaved={lastSaved} />
            </div>
            
            <ProgressIndicator progress={progress} />

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormContent 
                  currentStep={currentStep}
                  onPaymentSuccess={handlePaymentSuccess}
                />
                <FormNavigation 
                  currentStep={currentStep}
                  totalSteps={steps.length}
                  isSubmitting={isSubmitting}
                  onPrevious={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                  onNext={() => setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1))}
                  onSubmit={form.handleSubmit(onSubmit)}
                />
              </form>
            </Form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
